import { useEffect } from 'react';

interface UseFreelancerCleanupProps {
  selectedFreelancersForInvoice: any[];
  copiedInvoiceData: any;
  setTableRowsByFreelancer: React.Dispatch<React.SetStateAction<{ [freelancerId: string]: any[] }>>;
  setServiceTableDataByFreelancer: React.Dispatch<React.SetStateAction<{ [freelancerId: string]: any }>>;
  setDeductionsByFreelancer: React.Dispatch<React.SetStateAction<{ [freelancerId: string]: any[] }>>;
  setPensionDataByFreelancer: React.Dispatch<React.SetStateAction<{ [freelancerId: string]: { pension: boolean; pensionAmount: number } }>>;
  setHolidayDataByFreelancer: React.Dispatch<React.SetStateAction<{ [freelancerId: string]: { holidaySavings: boolean; holidaySavingsFee: number } }>>;
  setCalendarSelectedDatesByFreelancer: React.Dispatch<React.SetStateAction<{ [freelancerId: string]: Date[] }>>;
  setLastUsedPricingTypeByFreelancer: React.Dispatch<React.SetStateAction<{ [freelancerId: string]: string }>>;
  setExpandedSections: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  setRemovedCopiedWorkingHours: React.Dispatch<React.SetStateAction<Set<number>>>;
}

/**
 * Custom hook to cleanup freelancer data when freelancers are removed from invoice
 * Cleans up table rows, service data, deductions, pension, holiday, calendar dates, and other freelancer-specific state
 * @param props - Configuration object containing selectedFreelancersForInvoice and all state setters
 */
export const useFreelancerCleanup = ({
  selectedFreelancersForInvoice,
  copiedInvoiceData,
  setTableRowsByFreelancer,
  setServiceTableDataByFreelancer,
  setDeductionsByFreelancer,
  setPensionDataByFreelancer,
  setHolidayDataByFreelancer,
  setCalendarSelectedDatesByFreelancer,
  setLastUsedPricingTypeByFreelancer,
  setExpandedSections,
  setRemovedCopiedWorkingHours,
}: UseFreelancerCleanupProps) => {
  
  // Cleanup data when freelancers are removed
  useEffect(() => {
    // If we still have copiedInvoiceData (editing mode), don't clear the data
    // Only cleanup when explicitly removing freelancers while copiedInvoiceData is not present
    if (copiedInvoiceData && copiedInvoiceData.workingHours) {
      return;
    }

    // Get current freelancer IDs from selectedFreelancersForInvoice
    const currentFreelancerIds = selectedFreelancersForInvoice.map((friend: any) => 
      (friend.userId || friend.id).toString()
    );

    // Check and cleanup tableRowsByFreelancer
    setTableRowsByFreelancer(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach(freelancerId => {
        if (!currentFreelancerIds.includes(freelancerId)) {
          delete updated[freelancerId];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });

    // Check and cleanup serviceTableDataByFreelancer
    setServiceTableDataByFreelancer(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach(freelancerId => {
        if (!currentFreelancerIds.includes(freelancerId)) {
          delete updated[freelancerId];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });

    // Check and cleanup deductionsByFreelancer
    setDeductionsByFreelancer(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach(freelancerId => {
        if (!currentFreelancerIds.includes(freelancerId)) {
          delete updated[freelancerId];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });

    // Check and cleanup pensionDataByFreelancer
    setPensionDataByFreelancer(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach(freelancerId => {
        if (!currentFreelancerIds.includes(freelancerId)) {
          delete updated[freelancerId];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });

    // Check and cleanup holidayDataByFreelancer
    setHolidayDataByFreelancer(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach(freelancerId => {
        if (!currentFreelancerIds.includes(freelancerId)) {
          delete updated[freelancerId];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });

    // Check and cleanup calendarSelectedDatesByFreelancer
    setCalendarSelectedDatesByFreelancer(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach(freelancerId => {
        if (!currentFreelancerIds.includes(freelancerId)) {
          delete updated[freelancerId];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });

    // Check and cleanup lastUsedPricingTypeByFreelancer
    setLastUsedPricingTypeByFreelancer(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach(freelancerId => {
        if (!currentFreelancerIds.includes(freelancerId)) {
          delete updated[freelancerId];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });

    // Check and cleanup expandedSections
    setExpandedSections(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      Object.keys(updated).forEach(freelancerId => {
        if (!currentFreelancerIds.includes(freelancerId)) {
          delete updated[freelancerId];
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });

    // Check and cleanup removedCopiedWorkingHours - clear all removed indices when freelancer removed
    // This is more complex as we need to check copiedInvoiceData working hours
    if (copiedInvoiceData?.workingHours) {
      setRemovedCopiedWorkingHours(prev => {
        const updated = new Set(prev);
        let hasChanges = false;
        
        // For each removed index, check if it belongs to a removed freelancer
        prev.forEach(index => {
          const workingHour = copiedInvoiceData.workingHours[index];
          if (workingHour && !currentFreelancerIds.includes(workingHour.userId?.toString())) {
            // This working hour belongs to a removed freelancer, keep it in the set
            // Actually, we should remove it from the set since the freelancer is removed
            updated.delete(index);
            hasChanges = true;
          }
        });
        
        return hasChanges ? updated : prev;
      });
    }
  }, [
    selectedFreelancersForInvoice, 
    copiedInvoiceData,
    setTableRowsByFreelancer,
    setServiceTableDataByFreelancer,
    setDeductionsByFreelancer,
    setPensionDataByFreelancer,
    setHolidayDataByFreelancer,
    setCalendarSelectedDatesByFreelancer,
    setLastUsedPricingTypeByFreelancer,
    setExpandedSections,
    setRemovedCopiedWorkingHours
  ]);
};
